/**
 * SkillTree — Player RPG skill progression system.
 *
 * MINECRAFT DOESN'T HAVE THIS.
 *
 * As the player gains XP levels, they earn skill points.
 * Skill points unlock passive abilities across 4 branches:
 * - Guerrero (Combat): more damage, less knockback, critical chance
 * - Minero (Mining): mine faster, find more ores
 * - Explorador (Exploration): run faster, see further, breathe underwater longer
 * - Místico (Magic): regen health, double jump, resist potions
 *
 * Open with K key or /habilidades command.
 */

const SKILLS = {
  // GUERRERO (Combat tree)
  damage_1: {
    branch: 'Guerrero', tier: 1, cost: 1,
    name: 'Fuerza I', desc: '+10% daño cuerpo a cuerpo',
    effect: { damageMult: 1.1 },
  },
  damage_2: {
    branch: 'Guerrero', tier: 2, cost: 2, requires: ['damage_1'],
    name: 'Fuerza II', desc: '+25% daño cuerpo a cuerpo',
    effect: { damageMult: 1.25 },
  },
  crit_chance: {
    branch: 'Guerrero', tier: 2, cost: 2, requires: ['damage_1'],
    name: 'Precisión',  desc: '15% chance de golpe crítico',
    effect: { critChance: 0.15 },
  },
  armor_boost: {
    branch: 'Guerrero', tier: 3, cost: 3, requires: ['damage_2'],
    name: 'Resistencia', desc: 'Armadura absorbe 20% más daño',
    effect: { armorBonus: 1.2 },
  },

  // MINERO (Mining tree)
  mine_speed_1: {
    branch: 'Minero', tier: 1, cost: 1,
    name: 'Manos Rápidas', desc: '+15% velocidad de minado',
    effect: { mineSpeedMult: 1.15 },
  },
  mine_speed_2: {
    branch: 'Minero', tier: 2, cost: 2, requires: ['mine_speed_1'],
    name: 'Ojo de Minero', desc: '+30% velocidad de minado',
    effect: { mineSpeedMult: 1.3 },
  },
  fortune: {
    branch: 'Minero', tier: 2, cost: 2, requires: ['mine_speed_1'],
    name: 'Buena Suerte', desc: '20% chance de drop extra',
    effect: { extraDropChance: 0.2 },
  },
  xray_hint: {
    branch: 'Minero', tier: 3, cost: 3, requires: ['fortune'],
    name: 'Sentido Mineral', desc: 'Minerales brillan levemente',
    effect: { oreHint: true },
  },

  // EXPLORADOR (Exploration tree)
  fast_runner_1: {
    branch: 'Explorador', tier: 1, cost: 1,
    name: 'Corredor', desc: '+10% velocidad base',
    effect: { speedMult: 1.1 },
  },
  fast_runner_2: {
    branch: 'Explorador', tier: 2, cost: 2, requires: ['fast_runner_1'],
    name: 'Atleta', desc: '+20% velocidad base',
    effect: { speedMult: 1.2 },
  },
  breath_hold: {
    branch: 'Explorador', tier: 2, cost: 2, requires: ['fast_runner_1'],
    name: 'Respiración', desc: '+50% tiempo bajo agua',
    effect: { airSupplyMult: 1.5 },
  },
  fall_resist: {
    branch: 'Explorador', tier: 3, cost: 3, requires: ['fast_runner_2'],
    name: 'Gato', desc: 'Daño de caída reducido 50%',
    effect: { fallResistMult: 0.5 },
  },

  // MÍSTICO (Magic tree)
  regen_1: {
    branch: 'Místico', tier: 1, cost: 1,
    name: 'Vitalidad', desc: 'Regen pasiva +1 HP/10s',
    effect: { passiveRegen: 0.1 },
  },
  double_jump: {
    branch: 'Místico', tier: 2, cost: 2, requires: ['regen_1'],
    name: 'Doble Salto', desc: 'Puedes saltar 2 veces en el aire',
    effect: { doubleJump: true },
  },
  potion_resist: {
    branch: 'Místico', tier: 2, cost: 2, requires: ['regen_1'],
    name: 'Mente Clara', desc: 'Efectos negativos duran 50% menos',
    effect: { negativeEffectMult: 0.5 },
  },
  mana_shield: {
    branch: 'Místico', tier: 3, cost: 3, requires: ['potion_resist'],
    name: 'Escudo Arcano', desc: '10% chance de esquivar daño',
    effect: { dodgeChance: 0.1 },
  },
};

const STORAGE_KEY = 'fedeworld_skills';

export class SkillTree {
  constructor() {
    this.unlockedSkills = new Set();
    this.availablePoints = 0;
    this._lastKnownLevel = 0;
    this._loadState();
    this._buildUI();
  }

  _loadState() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      this.unlockedSkills = new Set(saved.unlocked || []);
      this.availablePoints = saved.points || 0;
      this._lastKnownLevel = saved.lastLevel || 0;
    } catch {}
  }

  _saveState() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      unlocked: Array.from(this.unlockedSkills),
      points: this.availablePoints,
      lastLevel: this._lastKnownLevel,
    }));
  }

  /**
   * Grant skill points when player levels up.
   * Player gets 1 point per level.
   */
  onLevelUp(newLevel) {
    if (newLevel > this._lastKnownLevel) {
      const gained = newLevel - this._lastKnownLevel;
      this.availablePoints += gained;
      this._lastKnownLevel = newLevel;
      this._saveState();
      if (this.onPointsGained) this.onPointsGained(gained);
    }
  }

  /**
   * Get combined effect multipliers from all unlocked skills.
   */
  getEffects() {
    const effects = {
      damageMult: 1, critChance: 0, armorBonus: 1,
      mineSpeedMult: 1, extraDropChance: 0, oreHint: false,
      speedMult: 1, airSupplyMult: 1, fallResistMult: 1,
      passiveRegen: 0, doubleJump: false, negativeEffectMult: 1, dodgeChance: 0,
    };
    for (const id of this.unlockedSkills) {
      const skill = SKILLS[id];
      if (!skill) continue;
      for (const [key, val] of Object.entries(skill.effect)) {
        if (typeof val === 'number') {
          // Multipliers stack multiplicatively, additives stack additively
          if (key.endsWith('Mult') || key === 'armorBonus') {
            effects[key] *= val;
          } else {
            effects[key] += val;
          }
        } else {
          effects[key] = val;
        }
      }
    }
    return effects;
  }

  canUnlock(id) {
    const skill = SKILLS[id];
    if (!skill) return false;
    if (this.unlockedSkills.has(id)) return false;
    if (this.availablePoints < skill.cost) return false;
    if (skill.requires) {
      for (const req of skill.requires) {
        if (!this.unlockedSkills.has(req)) return false;
      }
    }
    return true;
  }

  unlock(id) {
    if (!this.canUnlock(id)) return false;
    const skill = SKILLS[id];
    this.unlockedSkills.add(id);
    this.availablePoints -= skill.cost;
    this._saveState();
    this._render();
    if (this.onSkillUnlocked) this.onSkillUnlocked(skill);
    return true;
  }

  _buildUI() {
    this._overlay = document.createElement('div');
    this._overlay.style.cssText = `
      position:fixed; top:0; left:0; width:100%; height:100%;
      background:rgba(0,0,0,0.82); z-index:913; display:none;
      justify-content:center; align-items:center;
    `;

    const panel = document.createElement('div');
    panel.style.cssText = `
      background:#1A1A2E; border:4px solid #FFD700;
      padding:24px; width:720px; max-height:90vh; overflow-y:auto;
      font-family:'Courier New',monospace; color:#DDD;
    `;

    const header = document.createElement('div');
    header.innerHTML = `
      <div style="font-size:24px;font-weight:bold;color:#FFD700;text-align:center;margin-bottom:4px;">
        ⚔ Árbol de Habilidades ⚔
      </div>
    `;
    panel.appendChild(header);

    this._pointsDisplay = document.createElement('div');
    this._pointsDisplay.style.cssText = `
      text-align:center;font-size:14px;color:#AAA;margin-bottom:16px;
    `;
    panel.appendChild(this._pointsDisplay);

    this._treeContainer = document.createElement('div');
    this._treeContainer.style.cssText = 'display:grid;grid-template-columns:repeat(4,1fr);gap:12px;';
    panel.appendChild(this._treeContainer);

    const closeBtn = document.createElement('div');
    closeBtn.textContent = 'Cerrar';
    closeBtn.style.cssText = `
      margin-top:16px; padding:10px 0; text-align:center; cursor:pointer;
      background:#555; border:2px solid; border-color:#777 #333 #333 #777;
      font-size:14px; font-weight:bold;
    `;
    closeBtn.addEventListener('click', () => this.hide());
    panel.appendChild(closeBtn);

    this._overlay.appendChild(panel);
    document.body.appendChild(this._overlay);
  }

  _render() {
    this._pointsDisplay.textContent = `Puntos disponibles: ${this.availablePoints}`;

    this._treeContainer.innerHTML = '';
    const branches = ['Guerrero', 'Minero', 'Explorador', 'Místico'];
    const branchColors = { Guerrero: '#E74C3C', Minero: '#F39C12', Explorador: '#27AE60', Místico: '#9B59B6' };

    for (const branch of branches) {
      const col = document.createElement('div');
      const header = document.createElement('div');
      header.textContent = branch;
      header.style.cssText = `
        text-align:center;font-weight:bold;font-size:16px;color:${branchColors[branch]};
        padding:8px;border-bottom:2px solid ${branchColors[branch]};margin-bottom:8px;
      `;
      col.appendChild(header);

      const skills = Object.entries(SKILLS).filter(([_, s]) => s.branch === branch)
        .sort((a, b) => a[1].tier - b[1].tier);

      for (const [id, skill] of skills) {
        const unlocked = this.unlockedSkills.has(id);
        const available = this.canUnlock(id);

        const card = document.createElement('div');
        card.style.cssText = `
          padding:8px; margin-bottom:6px;
          background:${unlocked ? '#2D4A2D' : available ? '#3A3A3A' : '#1A1A1A'};
          border:2px solid ${unlocked ? '#4CAF50' : available ? branchColors[branch] : '#444'};
          cursor:${available ? 'pointer' : 'default'};
          opacity:${unlocked || available ? '1' : '0.5'};
        `;
        card.innerHTML = `
          <div style="font-weight:bold;font-size:13px;color:${unlocked ? '#4CAF50' : '#FFF'};">
            ${unlocked ? '✓' : ''} ${skill.name}
          </div>
          <div style="font-size:11px;color:#AAA;margin:2px 0;">${skill.desc}</div>
          <div style="font-size:10px;color:${unlocked ? '#4CAF50' : '#FFD700'};">
            ${unlocked ? 'Desbloqueada' : `${skill.cost} punto${skill.cost > 1 ? 's' : ''}`}
          </div>
        `;
        if (available) {
          card.addEventListener('click', () => this.unlock(id));
          card.addEventListener('mouseenter', () => { card.style.transform = 'scale(1.03)'; });
          card.addEventListener('mouseleave', () => { card.style.transform = 'scale(1)'; });
        }
        col.appendChild(card);
      }

      this._treeContainer.appendChild(col);
    }
  }

  show() {
    this._render();
    this._overlay.style.display = 'flex';
    this.isOpen = true;
  }

  hide() {
    this._overlay.style.display = 'none';
    this.isOpen = false;
  }
}
